import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  useSearchZoomInfoCompaniesMutation,
  useSearchZoomInfoContactsMutation,
  useImportZoomInfoLeadsMutation,
} from "state/api";
import { useGetAgentsQuery, useMoveLeadsFromPoolToUserMutation } from "state/managementApi";

const ZoomInfoManager = ({ onClose }) => {
  const theme = useTheme();

  // State
  const [commodity, setCommodity] = useState("");
  const [location, setLocation] = useState("");
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [importedLeads, setImportedLeads] = useState([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [lastClickedIndex, setLastClickedIndex] = useState(null);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // RTK Query hooks
  const [searchCompanies, { isLoading: loadingCompanies }] =
    useSearchZoomInfoCompaniesMutation();
  const [searchContacts, { isLoading: loadingContacts }] =
    useSearchZoomInfoContactsMutation();
  const [importLeads, { isLoading: importing }] =
    useImportZoomInfoLeadsMutation();
  const { data: agents } = useGetAgentsQuery();
  const [moveLeadsToUser] = useMoveLeadsFromPoolToUserMutation();

  // Search Companies
  const handleSearchCompanies = async () => {
    if (!commodity) {
      setNotification({
        open: true,
        message: "Please enter a commodity",
        severity: "warning",
      });
      return;
    }

    try {
      const result = await searchCompanies({ commodity, location }).unwrap();
      
      if (!result || !result.data) {
        throw new Error("No data returned from ZoomInfo");
      }
      
      const rows = (result.data || []).map((c) => ({
        id: c.id,
        name: c.name || "Unknown",
        industry: c.primaryIndustry?.join(", ") || "N/A",
        revenue: c.revenue || "N/A",
        location: c.city && c.state ? `${c.city}, ${c.state}` : "N/A",
        website: c.website || "",
      }));
      
      setCompanies(rows);
      setContacts([]);
      setSelectedCompanyId(null);
      setSelectedContactIds([]);
      
      setNotification({
        open: true,
        message: `Found ${rows.length} companies`,
        severity: "success",
      });
    } catch (error) {
      console.error('ZoomInfo Company Search Error:', error);
      
      let errorMessage = "Failed to search companies";
      
      if (error.status === 401 || error.status === 403) {
        errorMessage = "Authentication failed. Please check ZoomInfo credentials.";
      } else if (error.status === 429) {
        errorMessage = "Rate limit exceeded. Please try again later.";
      } else if (error.status === 500) {
        errorMessage = "ZoomInfo server error. Please try again.";
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setNotification({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  // Get Contacts for Company
  const handleGetContacts = async (companyId) => {
    setSelectedCompanyId(companyId);
    setSelectedContactIds([]);

    try {
      const result = await searchContacts({ companyId, location }).unwrap();
      
      if (!result || !result.data) {
        throw new Error("No contact data returned from ZoomInfo");
      }
      
      const rows = (result.data || []).map((c) => ({
        id: c.id,
        name: `${c.firstName || ""} ${c.lastName || ""}`.trim(),
        title: c.jobTitle || "N/A",
        email: c.email || "N/A",
        phone: c.phone || c.directPhoneNumber || "N/A",
        location: c.locationCity && c.locationState 
          ? `${c.locationCity}, ${c.locationState}` 
          : "N/A",
      }));
      
      setContacts(rows);
      
      setNotification({
        open: true,
        message: `Found ${rows.length} contacts`,
        severity: "success",
      });
    } catch (error) {
      console.error('ZoomInfo Contact Search Error:', error);
      
      let errorMessage = "Failed to fetch contacts";
      
      if (error.status === 401 || error.status === 403) {
        errorMessage = "Authentication failed. Please check ZoomInfo credentials.";
      } else if (error.status === 404) {
        errorMessage = "Company not found in ZoomInfo.";
      } else if (error.status === 429) {
        errorMessage = "Rate limit exceeded. Please try again later.";
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setNotification({
        open: true,
        message: errorMessage,
        severity: "error",
      });
      
      setContacts([]);
    }
  };

  // Import Selected Contacts
  const handleImportContacts = async () => {
    if (selectedContactIds.length === 0) {
      setNotification({
        open: true,
        message: "Please select contacts to import",
        severity: "warning",
      });
      return;
    }

    try {
      const result = await importLeads({ personIds: selectedContactIds }).unwrap();
      
      if (!result || !result.leads) {
        throw new Error("No leads were created");
      }
      
      const newLeads = result.leads || [];
      setImportedLeads(prev => [...prev, ...newLeads]);
      setSelectedContactIds([]);
      
      // Show detailed success/error message
      const successCount = newLeads.length;
      const totalCount = selectedContactIds.length;
      const failCount = totalCount - successCount;
      
      let message = result.message || `Successfully imported ${successCount} leads`;
      
      if (failCount > 0) {
        message += `. ${failCount} failed.`;
      }
      
      if (result.errors && result.errors.length > 0) {
        console.warn('Import errors:', result.errors);
      }
      
      setNotification({
        open: true,
        message: message,
        severity: failCount > 0 ? "warning" : "success",
      });
    } catch (error) {
      console.error('ZoomInfo Import Error:', error);
      
      let errorMessage = "Failed to import contacts";
      
      if (error.status === 401 || error.status === 403) {
        errorMessage = "Authentication failed. Please check ZoomInfo credentials.";
      } else if (error.status === 400) {
        errorMessage = "Invalid contact data. Please try different contacts.";
      } else if (error.status === 429) {
        errorMessage = "Rate limit exceeded. Please try again later.";
      } else if (error.status === 500) {
        errorMessage = "Server error while importing. Please try again.";
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setNotification({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  // Assign Leads to Agent
  const handleAssignLeads = async () => {
    if (selectedLeadIds.length === 0) {
      setNotification({
        open: true,
        message: "Please select leads to assign",
        severity: "warning",
      });
      return;
    }

    if (!selectedAgent) {
      setNotification({
        open: true,
        message: "Please select an agent",
        severity: "warning",
      });
      return;
    }

    try {
      const assignedCount = selectedLeadIds.length;
      const selectedAgentName = agents?.find(a => a._id === selectedAgent)?.name || "agent";
      
      await moveLeadsToUser({
        leadIds: selectedLeadIds,
        userIdTo: selectedAgent,
      }).unwrap();
      
      // Remove assigned leads from imported list
      setImportedLeads(prev => prev.filter(lead => !selectedLeadIds.includes(lead._id)));
      setSelectedLeadIds([]);
      setSelectedAgent("");
      
      setNotification({
        open: true,
        message: `Successfully assigned ${assignedCount} lead${assignedCount !== 1 ? 's' : ''} to ${selectedAgentName}`,
        severity: "success",
      });
    } catch (error) {
      console.error('Lead Assignment Error:', error);
      
      let errorMessage = "Failed to assign leads";
      
      if (error.status === 404) {
        errorMessage = "Selected agent not found. Please refresh and try again.";
      } else if (error.status === 400) {
        errorMessage = "Invalid lead selection. Please try again.";
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setNotification({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  // Handle Shift-Click Selection for Leads
  const handleLeadSelection = (leadId, index, event) => {
    if (event.shiftKey && lastClickedIndex !== null) {
      const start = Math.min(lastClickedIndex, index);
      const end = Math.max(lastClickedIndex, index);
      const rangeIds = importedLeads.slice(start, end + 1).map(lead => lead._id);
      setSelectedLeadIds(prev => [...new Set([...prev, ...rangeIds])]);
    } else {
      setSelectedLeadIds(prev =>
        prev.includes(leadId)
          ? prev.filter(id => id !== leadId)
          : [...prev, leadId]
      );
    }
    setLastClickedIndex(index);
  };



  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">ZoomInfo Lead Generator</Typography>
        {onClose && (
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        )}
      </Box>

      {/* Search Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                label="Commodity / Industry"
                variant="outlined"
                fullWidth
                value={commodity}
                onChange={(e) => setCommodity(e.target.value)}
                placeholder="e.g., Freight, Logistics, Produce"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Location (Optional)"
                variant="outlined"
                fullWidth
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g., Chicago, New York"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleSearchCompanies}
                disabled={loadingCompanies}
                sx={{ height: "56px" }}
              >
                {loadingCompanies ? <CircularProgress size={24} /> : "Search Companies"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Companies and Contacts Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={selectedCompanyId ? 6 : 12}>
          <Card sx={{ height: "60vh" }}>
            <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <Typography variant="h6" mb={1}>
                Companies ({companies.length})
              </Typography>
              <Box sx={{ flexGrow: 1, width: "100%", overflow: "auto" }}>
                {loadingCompanies ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer component={Paper}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Company</TableCell>
                          <TableCell>Industry</TableCell>
                          <TableCell>Revenue</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {companies.map((company) => (
                          <TableRow key={company.id} hover>
                            <TableCell>{company.name}</TableCell>
                            <TableCell>{company.industry}</TableCell>
                            <TableCell>{company.revenue}</TableCell>
                            <TableCell>{company.location}</TableCell>
                            <TableCell>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleGetContacts(company.id)}
                                sx={{ backgroundColor: theme.palette.secondary.main }}
                              >
                                View Contacts
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {selectedCompanyId && (
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "60vh" }}>
              <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6">
                    Contacts ({contacts.length})
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleImportContacts}
                    disabled={selectedContactIds.length === 0 || importing}
                  >
                    {importing ? (
                      <CircularProgress size={20} />
                    ) : (
                      `Import Selected (${selectedContactIds.length})`
                    )}
                  </Button>
                </Box>
                <Box sx={{ flexGrow: 1, width: "100%", overflow: "auto" }}>
                  {loadingContacts ? (
                    <Box display="flex" justifyContent="center" p={3}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox">
                              <Checkbox
                                indeterminate={selectedContactIds.length > 0 && selectedContactIds.length < contacts.length}
                                checked={contacts.length > 0 && selectedContactIds.length === contacts.length}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedContactIds(contacts.map(c => c.id));
                                  } else {
                                    setSelectedContactIds([]);
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Location</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {contacts.map((contact) => (
                            <TableRow key={contact.id} hover>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedContactIds.includes(contact.id)}
                                  onChange={() => {
                                    setSelectedContactIds(prev =>
                                      prev.includes(contact.id)
                                        ? prev.filter(id => id !== contact.id)
                                        : [...prev, contact.id]
                                    );
                                  }}
                                />
                              </TableCell>
                              <TableCell>{contact.name}</TableCell>
                              <TableCell>{contact.title}</TableCell>
                              <TableCell>{contact.email}</TableCell>
                              <TableCell>{contact.phone}</TableCell>
                              <TableCell>{contact.location}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Imported Leads Section */}
      {importedLeads.length > 0 && (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Imported Leads ({importedLeads.length})
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Select Agent</InputLabel>
                  <Select
                    value={selectedAgent}
                    onChange={(e) => setSelectedAgent(e.target.value)}
                    label="Select Agent"
                  >
                    {agents?.map((agent) => (
                      <MenuItem key={agent._id} value={agent._id}>
                        {agent.name} ({agent.role})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAssignLeads}
                  disabled={selectedLeadIds.length === 0 || !selectedAgent}
                >
                  Assign Selected ({selectedLeadIds.length})
                </Button>
              </Box>
            </Box>

            <Box sx={{ mb: 1 }}>
              <Button
                size="small"
                onClick={() => {
                  if (selectedLeadIds.length === importedLeads.length) {
                    setSelectedLeadIds([]);
                  } else {
                    setSelectedLeadIds(importedLeads.map(lead => lead._id));
                  }
                }}
              >
                {selectedLeadIds.length === importedLeads.length ? "Deselect All" : "Select All"}
              </Button>
            </Box>

            <Box sx={{ maxHeight: "50vh", overflow: "auto" }}>
              {importedLeads.map((lead, index) => (
                <Card
                  key={lead._id}
                  sx={{
                    mb: 1,
                    p: 2,
                    backgroundColor: selectedLeadIds.includes(lead._id)
                      ? theme.palette.action.selected
                      : "transparent",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                  onClick={(e) => handleLeadSelection(lead._id, index, e)}
                >
                  <Box display="flex" alignItems="center">
                    <Checkbox
                      checked={selectedLeadIds.includes(lead._id)}
                      onChange={() => {}}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Box ml={2} flex={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {lead.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {lead.contactName} {lead.contactLastName} - {lead.title || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {lead.contactEmail} | {lead.contactNumber || "No Phone"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {lead.city}, {lead.state} | {lead.commodity || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert severity={notification.severity} sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ZoomInfoManager;
